import { Field } from "@base-ui/react/field";
import { cn } from "@marketing-sites/utils/cn";
import type { ValidationRule } from "@marketing-sites/utils/validation";
import { composeValidate } from "@marketing-sites/utils/validation";
import {
  FieldErrors,
  type FieldErrorsProps,
} from "../form/validation/field-errors";

export type TextareaProps = Omit<Field.Control.Props, "render"> & {
  label?: string;
  fieldProps?: Omit<Field.Root.Props, "name" | "validate">;
  validations?: ValidationRule[];
  errorProps?: FieldErrorsProps["errorProps"];
  requiredMessage?: string;
  rows?: number;
};

/**
 * A labelled multi-line text field with built-in error display.
 *
 * Must be rendered inside a Base UI `<Form>` for validation and error
 * messages to work. Without it, `Field.Error` will never appear because
 * the validation lifecycle (`field.validate()` on submit) is not triggered.
 *
 * @see https://base-ui.com/react/components/field
 */
export function Textarea({
  name,
  label,
  className,
  fieldProps,
  validations,
  errorProps,
  requiredMessage,
  rows = 4,
  ...rest
}: TextareaProps) {
  return (
    <Field.Root
      name={name}
      validate={composeValidate(validations, rest.required, requiredMessage)}
      {...fieldProps}
    >
      {label && (
        <Field.Label className="label">
          {label} {rest.required && <span>*</span>}
        </Field.Label>
      )}
      <Field.Control
        name={name}
        render={<textarea rows={rows} />}
        className={cn("input textarea", className)}
        {...rest}
      />
      <FieldErrors errorProps={errorProps} requiredMessage={requiredMessage} />
    </Field.Root>
  );
}
